import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Dropdown, Input, Select, Tag, Avatar, Tooltip, Typography, message, Card, Row, Col, DatePicker, Divider } from 'antd';
import { PlusOutlined, LayoutOutlined, EnterOutlined, CloseOutlined, FilterOutlined, ClearOutlined, ProjectOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import TasksKanban from '../../components/ProjectBoard/TasksKanban';
import TaskDrawer from '../../components/ProjectBoard/TaskIssueDrawer';
import IssuesListView from '../../components/ProjectBoard/IssuesListView';
import { priorityOptions, queryToServerParams } from '../../components/ProjectBoard/issuesSearchUtils';
import useUrlFilters from 'hooks/useUrlFilters';

const { Search } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

// List view: the query box text, the selected saved view and the page live in
// the URL so a filtered task list can be shared.
const URL_FILTERS = {
  q: { type: 'string' },
  view: { type: 'string', default: 'all' },
  page: { type: 'number', default: 1 },
};

const Tasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const projectIdParam = searchParams.get('project_id');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'kanban'
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Pagination (list view) — server-side, 20 per page.
  const PAGE_SIZE = 20;
  const currentPage = filters.page;
  const setCurrentPage = useCallback((page) => setFilters({ page }), [setFilters]);
  const [totalCount, setTotalCount] = useState(0);

  // List-view search/filter params, derived from the query box text in the URL.
  const listQuery = filters.q || '';
  const listFilters = useMemo(() => queryToServerParams(listQuery), [listQuery]);
  const handleListFilters = useCallback((params, query) => {
    if ((query || '') !== listQuery) setFilters({ q: query, page: 1 });
  }, [listQuery, setFilters]);
  const handleViewChange = useCallback((key) => {
    if ((key || 'all') !== filters.view) setFilters({ view: key });
  }, [filters.view, setFilters]);

  // Quick add state
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [projectExists, setProjectExists] = useState(false);

  // Project info state
  const [projectInfo, setProjectInfo] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter options
  const [statuses, setStatuses] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [projects, setProjects] = useState([]);

  // Project info / members are needed for the page header — fetch immediately.
  useEffect(() => {
    const projectId = projectIdParam;
    if (projectId) {
      setProjectFilter(projectId);

      axios.get(`/project_board/projects/${projectId}/`)
        .then(res => {
          setProjectExists(true);
          setProjectInfo(res.data);
        })
        .catch(() => {
          setProjectExists(false);
          setProjectInfo(null);
        });

      axios.get(`/project_board/members/?project=${projectId}`).then(res => {
        setProjectMembers(res.data.results || res.data || []);
      }).catch(console.error);
    } else {
      setProjectFilter('');
      setProjectExists(false);
      setProjectInfo(null);
      setProjectMembers([]);
    }
  }, [projectIdParam]);

  // Assignees and (cross-project) projects are needed by the list-view inline
  // popovers, so they must load regardless of the kanban filter panel.
  const listOptionsLoadedRef = useRef({ projectId: null });
  useEffect(() => {
    const projectId = projectIdParam || '';
    if (listOptionsLoadedRef.current.projectId === projectId) return;
    listOptionsLoadedRef.current = { projectId };

    // Assignees source: project members when a project is in scope, global
    // employees only on the cross-project view.
    const assigneesReq = projectId
      ? axios.get(`/project_board/members/?project=${projectId}&page_size=1000`)
      : axios.get('/employee/employees/?page_size=1000');

    assigneesReq.then(res => {
      const rows = res.data.results || res.data || [];
      setAssignees(rows.map(r => ({
        id: r.user_id,
        user_name: r.user_name || r.user_email,
        employee_id: r.employee_id,
      })).filter(a => a.id));
    }).catch(console.error);

    if (!projectId) {
      axios.get('/project_board/projects/').then(res => {
        setProjects(res.data.results || res.data || []);
      }).catch(console.error);
    }
  }, [projectIdParam]);

  // Status / task-type lists for the kanban filter panel. Deferred to first
  // open to avoid the round-trip on initial paint.
  const filterOptionsLoadedRef = useRef({ projectId: null });
  useEffect(() => {
    if (!showFilters) return;
    const projectId = projectIdParam || '';
    if (filterOptionsLoadedRef.current.projectId === projectId) return;
    filterOptionsLoadedRef.current = { projectId };

    if (projectId) {
      axios.get(`/project_board/statuses/?project=${projectId}`).then(res => {
        setStatuses(res.data.results || res.data || []);
      }).catch(console.error);
      axios.get(`/project_board/task-types/?project=${projectId}`).then(res => {
        setTaskTypes(res.data.results || res.data || []);
      }).catch(console.error);
    } else {
      axios.get('/project_board/statuses/').then(res => {
        setStatuses(res.data.results || res.data || []);
      }).catch(console.error);
      axios.get('/project_board/task-types/').then(res => {
        setTaskTypes(res.data.results || res.data || []);
      }).catch(console.error);
    }
  }, [showFilters, projectIdParam]);

  // Any filter change resets to page 1; pagination only applies in list view.
  // Compared against the previous values so the page from the URL survives the
  // first render. (List query changes reset the page in handleListFilters.)
  const resetKey = JSON.stringify([searchTerm, projectIdParam, statusFilter, taskTypeFilter, priorityFilter, assigneeFilter, dueDateFilter, currentView]);
  const prevResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;
    setCurrentPage(1);
  }, [resetKey, setCurrentPage]);

  useEffect(() => {
    fetchTasks();
  }, [searchTerm, projectIdParam, statusFilter, taskTypeFilter, priorityFilter, assigneeFilter, dueDateFilter, currentView, currentPage, listFilters]);

  useEffect(() => {
    const editId = searchParams.get('editId');
    const viewId = searchParams.get('viewId');
    const createNew = searchParams.get('create');

    if (editId) {
      setSelectedTaskId(editId);
      setDrawerVisible(true);
    } else if (viewId) {
      setSelectedTaskId(viewId);
      setDrawerVisible(true);
    } else if (createNew === 'true') {
      setSelectedTaskId(null);
      setDrawerVisible(true);
    } else {
      setDrawerVisible(false);
    }
  }, [searchParams]);

  const fetchTasks = async () => {
    // Get project_id directly from URL to avoid race conditions
    const projectId = searchParams.get('project_id');

    try {
      setLoading(true);
      let url = '/project_board/tasks/';
      const params = new URLSearchParams();
      // Kanban needs the full set (it groups by status across the whole project).
      // List view paginates 20 per page server-side.
      if (currentView === 'kanban') {
        params.append('page_size', '1000');
        // Kanban filter panel — id-based filters.
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);
        if (taskTypeFilter) params.append('task_type', taskTypeFilter);
        if (priorityFilter !== '') params.append('priority', priorityFilter);
        if (assigneeFilter) params.append('assigned_to', assigneeFilter);
        if (dueDateFilter) params.append('due_date', dueDateFilter.format('YYYY-MM-DD'));
      } else {
        params.append('page', String(currentPage));
        params.append('page_size', String(PAGE_SIZE));
        // List view: newest activity first so a just-created (or edited)
        // task always lands at the top of page 1.
        params.append('ordering', '-updated_at,-created_at');
        // List view query box — search + status/type/priority/assignee all
        // run server-side so they filter the whole dataset, not one page.
        if (listFilters.search) params.append('search', listFilters.search);
        if (listFilters.status_name) params.append('status_name', listFilters.status_name);
        if (listFilters.task_type_name) params.append('task_type_name', listFilters.task_type_name);
        if (listFilters.priority !== '') params.append('priority', listFilters.priority);
        if (listFilters.assignee_name) params.append('assignee_name', listFilters.assignee_name);
      }
      if (projectId) params.append('project', projectId);
      url += `?${params.toString()}`;

      const response = await axios.get(url);
      const taskData = response.data.results || response.data;
      setTasks(taskData);
      setTotalCount(
        typeof response.data.count === 'number' ? response.data.count : taskData.length
      );
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTaskTypeFilter('');
    setPriorityFilter('');
    setAssigneeFilter('');
    setDueDateFilter(null);
    setSearchTerm('');
  };

  const hasActiveFilters = statusFilter || taskTypeFilter || priorityFilter !== '' || assigneeFilter || dueDateFilter || searchTerm;

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedTaskId(null);
    // Drop only the drawer params; project_id and the list filters stay.
    const params = new URLSearchParams(searchParams);
    params.delete('editId');
    params.delete('viewId');
    params.delete('create');
    setSearchParams(params);
  };

  const handleDrawerSuccess = () => {
    fetchTasks();
  };

  const handleQuickAdd = async () => {
    if (!quickAddTitle.trim()) {
      message.warning('Please enter a task title');
      return;
    }
    try {
      setQuickAddLoading(true);
      await axios.post('/project_board/tasks/', {
        title: quickAddTitle.trim(),
        project: projectFilter
      });
      message.success('Task created successfully');
      setQuickAddTitle('');
      setQuickAddVisible(false);
      fetchTasks();
    } catch (error) {
      message.error('Failed to create task');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const viewMenuItems = [
    { key: 'list', label: 'List View', onClick: () => setCurrentView('list') },
    { key: 'kanban', label: 'Kanban View', onClick: () => setCurrentView('kanban') },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '16px' }}>
        {projectFilter && projectInfo ? (
          <Card size="small" style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 8]} align="middle">
              <Col flex="auto">
                <Space direction="vertical" size={0}>
                  <Space align="center">
                    <ProjectOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                    <Title level={4} style={{ margin: 0 }}>{projectInfo.name}</Title>
                    <Tag color="blue">{projectInfo.project_code}</Tag>
                    {projectInfo.status && <Tag color={projectInfo.status === 'ACTIVE' ? 'green' : 'default'}>{projectInfo.status}</Tag>}
                  </Space>
                  {projectInfo.description && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{projectInfo.description}</Text>
                  )}
                </Space>
              </Col>
              <Col>
                <Space split={<Divider type="vertical" />} align="center">
                  {projectMembers.length > 0 && (
                    <Tooltip
                      title={
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Team Members</div>
                          {projectMembers.map((m, idx) => (
                            <div key={m.id || idx}>
                              {m.user_name} {m.employee_id && `(${m.employee_id})`}
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <span style={{ display: 'inline-block' }}>
                        <Avatar.Group maxCount={5} size="small">
                          {projectMembers.map((member) => (
                            <Avatar
                              key={member.id}
                              style={{
                                backgroundColor: ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16'][
                                  member.user_name?.charCodeAt(0) % 5
                                ],
                                fontSize: 11
                              }}
                              size="small"
                            >
                              {member.user_name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                            </Avatar>
                          ))}
                        </Avatar.Group>
                      </span>
                    </Tooltip>
                  )}
                  {projectInfo.owner_name && (
                    <Tooltip title="Owner">
                      <Space size={4}>
                        <UserOutlined style={{ color: '#888' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{projectInfo.owner_name}</Text>
                      </Space>
                    </Tooltip>
                  )}
                  {projectInfo.due_date && (
                    <Tooltip title="Due Date">
                      <Space size={4}>
                        <CalendarOutlined style={{ color: '#888' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(projectInfo.due_date).format('MMM DD, YYYY')}</Text>
                      </Space>
                    </Tooltip>
                  )}
                  <Button size="small" onClick={() => navigate(`/projects/view/${projectFilter}`)}>
                    View Project
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        ) : (
          <Title level={4} style={{ margin: 0, marginBottom: 16 }}>All Tasks</Title>
        )}
      </div>

      {/* View toggle + New Task (always visible) */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div />
        <Space>
          <Dropdown menu={{ items: viewMenuItems }}>
            <Button icon={<LayoutOutlined />}>
              {currentView === 'list' ? 'List View' : 'Kanban View'}
            </Button>
          </Dropdown>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('create', 'true');
              setSearchParams(params);
            }}
          >
            New Task
          </Button>
        </Space>
      </div>

      {/* Legacy toolbar + filters — Kanban only */}
      {currentView === 'kanban' && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <Space wrap>
            <Search
              placeholder="Search tasks..."
              onSearch={setSearchTerm}
              allowClear
              style={{ width: 250 }}
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              type={showFilters ? 'primary' : 'default'}
            >
              Filters {hasActiveFilters && <Tag color="blue" style={{ marginLeft: 4 }}>Active</Tag>}
            </Button>
            {hasActiveFilters && (
              <Button icon={<ClearOutlined />} onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Space>
        </div>
      )}

      {/* Filters Panel — Kanban only */}
      {currentView === 'kanban' && showFilters && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 12]}>
            {!projectFilter && (
              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Project</Text>
                <Select
                  placeholder="All Projects"
                  value={projectFilter || undefined}
                  onChange={(val) => {
                    if (val) {
                      setSearchParams({ project_id: val });
                    } else {
                      setSearchParams({});
                    }
                  }}
                  allowClear
                  style={{ width: '100%' }}
                  size="small"
                  showSearch
                  optionFilterProp="label"
                  optionLabelProp="label"
                >
                  {projects.map(p => (
                    <Option key={p.id} value={p.id} label={p.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{p.project_code}</Tag>
                        <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={6} lg={4}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Status</Text>
              <Select
                placeholder="All Statuses"
                value={statusFilter || undefined}
                onChange={setStatusFilter}
                allowClear
                style={{ width: '100%' }}
                size="small"
              >
                {statuses.map(s => (
                  <Option key={s.id} value={s.id}>
                    <Tag color={s.color} style={{ margin: 0 }}>{s.name}</Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Task Type</Text>
              <Select
                placeholder="All Types"
                value={taskTypeFilter || undefined}
                onChange={setTaskTypeFilter}
                allowClear
                style={{ width: '100%' }}
                size="small"
              >
                {taskTypes.map(t => (
                  <Option key={t.id} value={t.id}>{t.name}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Priority</Text>
              <Select
                placeholder="All Priorities"
                value={priorityFilter !== '' ? priorityFilter : undefined}
                onChange={(val) => setPriorityFilter(val !== undefined ? val : '')}
                allowClear
                style={{ width: '100%' }}
                size="small"
              >
                {priorityOptions.map(p => (
                  <Option key={p.value} value={p.value}>
                    <Tag color={p.color} style={{ margin: 0 }}>{p.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Assignee</Text>
              <Select
                placeholder="All Assignees"
                value={assigneeFilter || undefined}
                onChange={setAssigneeFilter}
                allowClear
                style={{ width: '100%' }}
                size="small"
                showSearch
                optionFilterProp="label"
              >
                {assignees.filter(a => a.id).map(a => (
                  <Option key={a.id} value={a.id} label={a.user_name}>
                    {a.user_name} {a.employee_id && `(${a.employee_id})`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Due Date</Text>
              <DatePicker
                placeholder="Select date"
                value={dueDateFilter}
                onChange={setDueDateFilter}
                style={{ width: '100%' }}
                size="small"
                allowClear
              />
            </Col>
          </Row>
        </Card>
      )}

      {currentView === 'list' ? (
        <>
          {/* Quick Add Row - only show when project_id exists and project is valid */}
          {projectFilter && projectExists && (
            <div style={{ marginBottom: 12 }}>
              {quickAddVisible ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: '#fafafa',
                    borderRadius: 6,
                    border: '1px dashed #d9d9d9'
                  }}
                >
                  <PlusOutlined style={{ color: '#1890ff' }} />
                  <Input
                    placeholder="Enter task title and press Enter..."
                    value={quickAddTitle}
                    onChange={(e) => setQuickAddTitle(e.target.value)}
                    onPressEnter={handleQuickAdd}
                    style={{ flex: 1 }}
                    size="small"
                    autoFocus
                    disabled={quickAddLoading}
                  />
                  <Tooltip title="Save (Enter)">
                    <Button
                      type="primary"
                      size="small"
                      icon={<EnterOutlined />}
                      onClick={handleQuickAdd}
                      loading={quickAddLoading}
                    />
                  </Tooltip>
                  <Tooltip title="Cancel (Esc)">
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setQuickAddVisible(false);
                        setQuickAddTitle('');
                      }}
                    />
                  </Tooltip>
                </div>
              ) : (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setQuickAddVisible(true)}
                  style={{ width: '100%' }}
                  size="small"
                >
                  Quick Add Task
                </Button>
              )}
            </div>
          )}

          <IssuesListView
            tasks={tasks}
            loading={loading}
            taskTypes={taskTypes}
            assignees={assignees}
            projects={projects}
            showProjectFilter={!projectFilter}
            projectId={projectFilter || null}
            canManageViews={projectInfo?.is_owner === true}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onServerSearch={handleListFilters}
            initialQuery={listQuery}
            initialViewKey={filters.view}
            onViewChange={handleViewChange}
            onOpenTask={(id) => {
              const params = new URLSearchParams(searchParams);
              params.set('editId', id);
              setSearchParams(params);
            }}
            onTaskUpdated={(id, patch) => {
              setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
            }}
          />
        </>
      ) : (
        <TasksKanban projectId={projectFilter} onTaskUpdate={fetchTasks} />
      )}

      <TaskDrawer
        visible={drawerVisible}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
        onSuccess={handleDrawerSuccess}
        onCreated={(id) => {
          const params = new URLSearchParams(searchParams);
          params.delete('create');
          if (id) params.set('editId', id);
          setSearchParams(params);
          fetchTasks();
        }}
        projectId={projectFilter || null}
      />
    </div>
  );
};

export default Tasks;
